import React, { FC, useCallback, useMemo } from "react";
import style from "./style.scss";
import { Button } from "@xapp/components";

export interface WidgetValuePickerChildrenProps<T> {
    readonly value: T | undefined;
    readonly override: boolean;
    readonly required: boolean;
    disabled?: boolean;
    onChange(value: T): void;
}

export interface WidgetValuePickerProps<T> {
    readonly value: T | undefined;
    /**
     * Adding a default value will allow the user to revert to the default value
     */
    readonly defaultValue?: T | undefined;
    /**
     * Optional label to override the default label of "Override"
     */
    readonly overrideLabel?: string;
    /**
     * Optional label to override the default label of "Default"
     */
    readonly defaultLabel?: string;
    readonly required?: boolean;
    readonly children: FC<WidgetValuePickerChildrenProps<T>>;
    readonly disabled?: boolean;
    /**
     * If the default is passed back when the value is cleared, as opposed to undefined.
     */
    readonly passBackDefault?: boolean;
    onChange(value: T | undefined): void;
}

export function WidgetValuePicker<T>(props: WidgetValuePickerProps<T>): JSX.Element {

    const { value, defaultValue, required, disabled, onChange, passBackDefault } = props;

    const hasValue = (value !== undefined) && (value !== null);
    const hasDefaultValue = !!defaultValue;

    const handleClear = useCallback(() => {
        if (hasDefaultValue && passBackDefault) {
            onChange(defaultValue);
        } else {
            onChange(undefined);
        }
    }, [defaultValue, hasDefaultValue, onChange, passBackDefault]);

    const handleOverride = useCallback(() => {
        onChange(defaultValue);
    }, [onChange, defaultValue]);

    const childProps = useMemo<WidgetValuePickerChildrenProps<T>>(() => ({
        onChange,
        override: hasDefaultValue && !hasValue,
        required: required ?? true,
        value: value ?? defaultValue,
        disabled: disabled
    }), [onChange, required, value, defaultValue, disabled, hasDefaultValue, hasValue]);

    const overrideLabel = props.overrideLabel || "Override";
    const defaultLabel = props.defaultLabel || "Default";

    const disabledButton = hasDefaultValue ? defaultValue === value : !hasValue;

    return (
        <>
            {React.createElement<WidgetValuePickerChildrenProps<T>>(props.children, childProps)}

            {/* If we have a default value, we show the button to allow them to revert */}
            {hasDefaultValue &&
                <div style={{ display: "flex", alignItems: "center" }}>
                    <Button
                        variation="default"
                        disabled={disabledButton}
                        className={!hasValue ? style.widgetValueOverride : style.widgetValueClear}
                        label={!hasValue ? overrideLabel : defaultLabel}
                        onClick={!hasValue ? handleOverride : handleClear} />
                </div>
            }
        </>
    );
}
